import { DeleteDialog } from '@retail/components';
import { UniversalFormProvider } from '@shared/components';
import { CreatePriceSuggestionConditionDialog } from './Create';
import { ConditionsNew } from '@retail/calculus/types';
import { useConditionActions } from '@retail/calculus/context';
import { dateRangeSchema } from '@retail/calculus/components';
import { useAppTFunction } from '@retail/app/i18n';
import { PriceSuggestionTypeWithCount } from '@retail/price-suggestion/types';
import { EditItemPriceSuggestionConditionDialog, EditPriceSuggestionConditionDialog } from './Edit';

interface Props {
  supplierNumber?: number;
  priceSuggestionTypes: PriceSuggestionTypeWithCount[];
  createCondition: boolean;
  editCondition?: ConditionsNew;
  deactivateConditionId?: number;
  deleteConditionId?: number;
  closeCreate?: () => void;
  closeEdit: () => void;
  closeDeactivate: () => void;
  closeDelete: () => void;
}

export function PriceSuggestionConditionActions({
  supplierNumber,
  priceSuggestionTypes,
  createCondition,
  editCondition,
  deactivateConditionId,
  deleteConditionId,
  closeCreate,
  closeEdit,
  closeDeactivate,
  closeDelete,
}: Props) {
  const t = useAppTFunction();
  const { removeCondition } = useConditionActions();

  return (
    <>
      {createCondition && closeCreate && (
        <CreatePriceSuggestionConditionDialog
          open
          supplierNumber={supplierNumber}
          priceSuggestionTypes={priceSuggestionTypes}
          onClose={closeCreate}
        />
      )}
      {editCondition && (
        <UniversalFormProvider
          schema={dateRangeSchema(t)}
          defaultValues={dateRangeSchema(t).getDefault()}
        >
          {editCondition.bindingType === 'MG_SUPPLIER_ITEM' && editCondition.supplier ? (
            <EditItemPriceSuggestionConditionDialog
              supplierNumber={editCondition.supplier.supplierNumber}
              itemName={editCondition.bindingTargetName}
              mgItemNumber={Number(editCondition.bindingTargetId)}
              currentPriority={editCondition.priceSourceCondition?.priorities?.map((prio) => prio.priceSuggestionType) ?? []}
              currentValidFrom={editCondition.priceSourceCondition?.validFrom}
              onClose={closeEdit}
            />
          ) : (
            <EditPriceSuggestionConditionDialog
              open
              bindingName={editCondition.bindingTargetName}
              currentPriority={editCondition.priceSourceCondition?.priorities.map((prio) => prio.priceSuggestionType) ?? []}
              priceSuggestionTypes={priceSuggestionTypes.map((ps) => ps.suggestionType)}
              conditionBinding={editCondition.getBindingLevel()}
              currentValidFrom={editCondition.priceSourceCondition?.validFrom}
              priceSuggestionCount={priceSuggestionTypes}
              onClose={closeEdit}
            />
          )}
        </UniversalFormProvider>
      )}
      {deleteConditionId && (
        <DeleteDialog
          isOpen
          dialogTitle={t('calculus.actions.deleteFromDraft')}
          message={t('calculus.actions.confirmRemoval')}
          onDelete={() => removeCondition([deleteConditionId], 'delete')}
          onClose={closeDelete}
        />
      )}
      {deactivateConditionId && (
        <DeleteDialog
          isOpen
          dialogTitle={t('calculus.actions.deactivateCondition')}
          message={t('calculus.actions.confirmDeactivation')}
          onDelete={() => removeCondition([deactivateConditionId], 'deactivate')}
          onClose={closeDeactivate}
        />
      )}
    </>
  );
}
